//import external libraries
import * as React from "react";
import { useState, useContext, memo } from "react";
import { useNavigate } from "react-router-dom";
import Geocode from "react-geocode";
import { useTranslation } from "react-i18next";

//import external services and resources
import config from "../../utils/config";
import { appCtx } from "../../utils/hooks/appContext";
import { DataGrid } from "@mui/x-data-grid";
import { filterContext } from "../../utils/hooks/filterContext";

//import MUI components
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";

import Stack from "@mui/material/Stack";
import { Grid } from "@mui/material";
import {
  GridToolbarContainer,
  GridToolbarFilterButton,
  getGridNumericOperators,
  getGridStringOperators,
  getGridDateOperators,
} from "@mui/x-data-grid";

Geocode.setApiKey(config.google_map_api_key);
Geocode.enableDebug();

const ProjectsTableCombination = memo((props) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const moment = require("moment");

  const { setProjectId } = useContext(appCtx);
  const { filterParameters, setFilterParameters } = useContext(filterContext);

  const [rowModesModel, setRowModesModel] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const indexOfLastItem = currentPage * 10;
  const indexOfFirstItem = indexOfLastItem - 10;
  const currentProjects = props.projects.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  function CustomToolbar() {
    return (
      <GridToolbarContainer
        className="MuiFormControl-root MuiTextField-root css-i44wyl"
        sx={{
          "&.css-17vbkzs-MuiFormControl-root-MuiTextField-root": {
            marginTop: "0px !important",
            backgroundColor: "yellow",
          },
        }}
      >
        <GridToolbarFilterButton />
      </GridToolbarContainer>
    );
  }

  const RenderButtonOpen = (id) => {
    return (
      <Button
        onClick={(e) => {
          toProjectInfo(id);
        }}
        sx={{ color: theme.palette.secondary.main }}
      >
        {t("projectTableBtn.open")}
      </Button>
    );
  };

  const toProjectInfo = (id) => {
    setProjectId(id.row.id);
    console.log(id.row);

    window.scrollTo(0, 0);
    navigate(
      `/project-item/${id.row.id}?projectNumber=${id.row.number}&projectName=${id.row.name}`
    );
  };

  const columns = [
    {
      field: "btnOpen",
      headerName: t("projectTableheaderName.open"),
      flex: 1,
      minWidth: 60,
      cellClassName: "actions",
      renderCell: RenderButtonOpen,
      headerClassName: "header-style",
      filterable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "id",
      headerName: t("projectTableheaderName.projectNumber"),
      minWidth: 170,
      flex: 1,
      headerAlign: "center",
      align: "center",
      headerClassName: "header-style",
      filterOperators: getGridStringOperators().filter(
        (operator) =>
          operator.value === "contains" || operator.value === "isAnyOf"
      ),
    },
    {
      field: "name",
      headerName: t("projectTableheaderName.projectName"),
      flex: 3,
      minWidth: 100,
      editable: true,
      headerAlign: "center",
      align: "center",
      headerClassName: "header-style",
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),
    },
    {
      field: "createdAt",
      headerName: t("projectTableheaderName.createdAt"),
      type: "date",
      flex: 2,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      headerClassName: "header-style",
      valueGetter: (params) => {
        const localTime = new Date(
          new Date(params.row.createdAt).getTime() - params.row.timeZone * 60000
        );

        return moment(localTime).format("DD/MM/YYYY HH:mm:ss");
      },

      filterOperators: getGridDateOperators().filter(
        (operator) =>
          operator.value === "is" ||
          operator.value === "before" ||
          operator.value === "onOrBefore" ||
          operator.value === "after" ||
          operator.value === "onOrAfter" ||
          operator.value === "not"
      ),
    },
    {
      field: "address",
      headerName: t("projectTableheaderName.address"),
      flex: 4,
      editable: true,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      headerClassName: "header-style",
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),
    },
    {
      field: "city",
      headerName: t("projectTableheaderName.city"),
      flex: 2,
      editable: true,
      headerAlign: "center",
      align: "center",
      minWidth: 100,
      headerClassName: "header-style",
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),
    },
    {
      field: "country",
      headerName: t("projectTableheaderName.country"),
      flex: 2,
      editable: true,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      headerClassName: "header-style",
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),
    },

    {
      field: "numberOfUnits",
      headerName: t("projectTableheaderName.numberOfUnits"),
      type: "number",
      flex: 1,
      minWidth: 140,
      headerAlign: "center",
      align: "center",
      headerClassName: "header-style",
      // filterOperators: ["!=", "<", ">"],
      filterOperators: getGridNumericOperators().filter(
        (operator) =>
          operator.value === ">" ||
          operator.value === "<" ||
          operator.value === "!=" ||
          operator.value === "="
      ),
    },

    {
      field: "totalPrice",
      headerName: t("projectTableheaderName.totalCosts"),
      type: "number",
      flex: 1,
      headerAlign: "center",
      align: "center",
      headerClassName: "header-style",
      valueFormatter: ({ value }) => `${value.toFixed(2)} €`,
      minWidth: 160,
      filterOperators: getGridNumericOperators().filter(
        (operator) =>
          operator.value === ">" ||
          operator.value === "<" ||
          operator.value === "!=" ||
          operator.value === "="
      ),
    },
  ];

  const handleFilterChange = (newFilterModel) => {
    if (newFilterModel.items.length === 0) {
      setFilterParameters({
        items: [
          {
            columnField: "id",
            operator: "contains",
            operatorValue: "contains",
            value: "",
          },
        ],
      });
    } else {
      setFilterParameters(newFilterModel);
    }
  };

  return (
    <div style={{ lineHeight: "1" }}>
      <Box>
        <Stack
          spacing={2}
          sx={{ padding: "0 10px 10px 10px", display: { sm: "none" } }}
        >
          {currentProjects.map((row) => (
            <Box
              className="projectCard"
              onClick={() => toProjectInfo({ row })}
              key={row.id}
              sx={{
                border: "1px solid #ccc",
                padding: "15px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "16px",
                lineHeight: "1.5",
                boxShadow: "3px 3px 4px 0px #555",
              }}
            >
              <Box>
                {t("projectTableheaderName.projectName")}:{" "}
                <strong>{row.name}</strong>
              </Box>
              <Box>
                {t("projectTableheaderName.createdAt")}:{" "}
                <strong>
                  {moment(row.createdAt).format("DD/MM/YYYY HH:mm:ss")}
                </strong>
              </Box>
              <Box>
                {t("projectTableheaderName.address")}:{" "}
                <strong>{row.address}</strong>
              </Box>
              <Box>
                {t("projectTableheaderName.city")}: <strong>{row.city}</strong>
              </Box>
              <Box>
                {t("projectTableheaderName.country")}:{" "}
                <strong>{row.country}</strong>
              </Box>
              <Box>
                {t("projectTableheaderName.numberOfUnits")}:{" "}
                <strong>{row.numberOfUnits}</strong>
              </Box>
              <Box>
                {t("projectTableheaderName.totalCosts")}:{" "}
                <strong>{row.totalPrice}</strong>
              </Box>
            </Box>
          ))}

          <Box
            className="pagination"
            sx={{
              display: "flex",
              width: "100%",
              flexWrap: "wrap",
              gap: "10px",
              justifyContent: "center",
              marginBottom: "25px",
              marginTop: "15px",
            }}
          >
            {Array.from({ length: Math.ceil(props.projects.length / 10) }).map(
              (_, index) => (
                <button
                  style={{
                    fontSize: "20px",
                    borderRadius: "5px",
                    padding: "5px 10px",
                    color: "white",
                    border: "none",
                    backgroundColor: theme.palette.secondary.main,
                  }}
                  key={index}
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </button>
              )
            )}
          </Box>
        </Stack>
      </Box>

      <Grid
        container
        sx={{
          display: { xs: "none", sm: "flex" },
          height: 500,
          width: "100%",
          "& .actions": {
            color: "text.secondary",
          },
          "& .textPrimary": {
            color: "text.primary",
          },
        }}
      >
        <Grid item xs={12}>
          <DataGrid
            rows={props.projects}
            columns={columns}
            editMode="row"
            rowModesModel={rowModesModel}
            rowsPerPageOptions={[10, 20, 100]}
            localeText={{
              MuiTablePagination: {
                labelRowsPerPage: t("tableRows.rowsPerPage"),
                labelDisplayedRows: ({ from, to, count }) =>
                  `${from} - ${to} ${t("tableRows.of")} ${count}`,
              },
            }}
            components={{
              Toolbar: CustomToolbar,
              NoRowsOverlay: () => (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  {t("projectitemrevision.noRows")}
                </Stack>
              ),
            }}
            filterModel={filterParameters}
            onFilterModelChange={handleFilterChange}
            componentsProps={{
              toolbar: { setRowModesModel },
              filterPanel: { sx: { maxWidth: "100vw" } },
            }}
            experimentalFeatures={{ newEditingApi: true }}
            sx={{
              ".MuiDataGrid-columnHeaders": {
                backgroundColor: theme.palette.secondary.main,
              },
              ".MuiTablePagination-select": {
                "margin-top": "-3px",
              },
              ".MuiTablePagination-selectIcon": {
                marginTop: "-3px",
              },

              ".MuiFormControl-root.MuiFormControl-marginNormal.MuiFormControl-fullWidth.MuiTextField-root.css-17vbkzs-MuiFormControl-root-MuiTextField-root":
                {
                  display: "none",
                },
              ".MuiDataGrid-columnHeaderTitle.css-1jbbcbn-MuiDataGrid-columnHeaderTitle":
                {
                  fontWeight: "bold",
                  paddingLeft: "5px",
                },
            }}
          />
        </Grid>
      </Grid>
    </div>
  );
});

//ProjectsTableCombination.whyDidYouRender = true;

export default ProjectsTableCombination;
